import React, { useState, useEffect } from 'react';
import Map, { Source, Layer } from 'react-map-gl/mapbox';
import { useAuth0 } from '@auth0/auth0-react';
import { logActivity } from '../services/api';
import 'mapbox-gl/dist/mapbox-gl.css';

// Types for county properties (from dynamic backend response)
interface CountyProperties {
  // Original GeoJSON properties
  STATE?: string;
  COUNTY?: string;
  NAME?: string;
  LSAD?: string;
  CENSUSAREA?: number;
  GEO_ID?: string;
  FIPS?: string;
  state?: string;
  
  // Dynamic facility aggregation data
  total_facilities?: number;
  total_tanks?: number;
  active_tanks?: number;
  facilities_with_leaks?: number;
  total_leaks?: number;
  avg_tank_age?: number;
  max_tank_age?: number;
  avg_protection_score?: number;
  avg_risk_score?: number;
  total_capacity?: number;
  leak_rate?: number;
  
  // Release data (for states)
  total_releases?: number;
  open_releases?: number;
  closed_releases?: number;
  open_release_rate?: number;
  has_release_data?: boolean;
  
  // Metadata from dynamic endpoint
  has_facility_data?: boolean;
  match_key?: string;
}

interface Facility {
  Facility_ID: string;
  Name: string;
  Address: string;
  City: string;
  County: string;
  State: string;
  Zip_Code: string;
  Latitude: number;
  Longitude: number;
  FIPS: string;
  MAPPING_METHOD: string;
  HAS_LEAK: boolean;
  TOTAL_TANK_COUNT: number;
  ACTIVE_TANK_COUNT: number;
  AVG_TANK_AGE: number;
  MAX_TANK_AGE: number;
  PROTECTION_SCORE: number;
  TOTAL_LEAK_COUNT: number;
  TOTAL_CAPACITY: number;
  risk_score: number;
}

const CountyMapView: React.FC = () => {
  const { isAuthenticated, user } = useAuth0();
  const [geoData, setGeoData] = useState<any>(null);
  const [selectedMetric, setSelectedMetric] = useState<string>('total_facilities');
  const [hoveredCounty, setHoveredCounty] = useState<CountyProperties | null>(null);
  const [selectedCounty, setSelectedCounty] = useState<CountyProperties | null>(null);
  const [loading, setLoading] = useState(true);
  const [facilityData, setFacilityData] = useState<Facility[]>([]);
  const [countyFacilities, setCountyFacilities] = useState<Facility[]>([]);
  const [colorScale, setColorScale] = useState<string>('percentile');
  const [mapStyle, setMapStyle] = useState<string>('streets');
  const [boundaryLevel, setBoundaryLevel] = useState<string>('states');

  // Mapbox token from environment
  const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;

  // Load data with caching
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated || !user) return;

      // Check if data is already loaded for this boundary level
      const cacheKey = `map_data_${boundaryLevel}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);
      const now = Date.now();
      const cacheMaxAge = 5 * 60 * 1000; // 5 minutes

      // Use cached data if available and not expired
      if (cachedData && cacheTimestamp && (now - parseInt(cacheTimestamp)) < cacheMaxAge) {
        try {
          const parsedData = JSON.parse(cachedData);
          console.log(`Using cached ${boundaryLevel} data`);
          setGeoData(parsedData);
          setLoading(false);
          return;
        } catch (error) {
          console.warn('Failed to parse cached data, fetching fresh data');
        }
      }

      try {
        console.log(`Loading fresh ${boundaryLevel} data...`);
        setLoading(true);
        
        // Load GeoJSON with boundaries and tank data using cached endpoint
        console.log(`Fetching cached GeoJSON data for ${boundaryLevel} from backend API`);
        const geoResponse = await fetch(`${process.env.REACT_APP_API_URL}/geojson-cached/${boundaryLevel}?filter_type=base&min_facility_count=1`);
        
        if (geoResponse.ok) {
          console.log('GeoJSON response OK, parsing JSON...');
          const geoJson = await geoResponse.json();
          
          // Validate and log GeoJSON structure
          const featuresCount = geoJson.features?.length || 0;
          const hasValidFeatures = featuresCount > 0;
          
          console.log(`Loaded cached GeoJSON data: ${featuresCount} ${boundaryLevel}`);
          console.log('GeoJSON metadata:', geoJson.metadata);
          
          if (!hasValidFeatures) {
            console.warn('Cached GeoJSON loaded but has no features');
          }
          
          // Log statistics from cached endpoint
          const areasWithData = geoJson.features?.filter((f: any) => 
            f.properties && f.properties.has_facility_data
          ) || [];
          
          console.log(`${boundaryLevel} with storage tank data: ${areasWithData.length}`);
          
          setGeoData(geoJson);

          // Cache the data for future tab switches
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(geoJson));
            sessionStorage.setItem(`${cacheKey}_timestamp`, now.toString());
            console.log(`Cached ${boundaryLevel} data for future use`);
          } catch (error) {
            console.warn('Failed to cache data:', error);
          }
        } else {
          console.error('Failed to fetch GeoJSON', {
            status: geoResponse.status,
            statusText: geoResponse.statusText
          });
        }

        // Note: Facility data is now included in the dynamic GeoJSON response
        // Individual facility-level data will be loaded on-demand when counties are clicked
        console.log('County aggregation data included in GeoJSON response');
        
        // Log map load activity
        console.log('County map loaded for user:', user?.email);
        if (user) {
          logActivity('map_loaded', user);
        }
        
      } catch (error) {
        console.error('Error loading county map data:', error);
      } finally {
        setLoading(false);
        console.log('County map data loading complete');
      }
    };
    
    loadData();
  }, [isAuthenticated, user, boundaryLevel]);

  // Handle area (county/state) click
  const handleAreaClick = (event: any) => {
    console.log(`${boundaryLevel} click event`, {
      featuresCount: event.features?.length || 0,
      lngLat: event.lngLat,
      point: event.point
    });
    
    const feature = event.features?.[0];
    if (feature?.properties) {
      const props = feature.properties as CountyProperties;
      const areaName = boundaryLevel === 'counties' ? `${props.NAME}, ${props.state || props.STATE || 'Unknown'}` : props.NAME || 'Unknown';
      console.log(`${boundaryLevel} selected: ${areaName}`, {
        facilities: props.total_facilities,
        leakRate: props.leak_rate,
        tanks: props.total_tanks
      });
      setSelectedCounty(props);
      
      // Load facilities for this area from backend API (only works for counties currently)
      const loadAreaFacilities = async () => {
        try {
          // Only load individual facilities for counties (states would be too many)
          if (boundaryLevel === 'counties') {
            // Extract FIPS from GeoJSON (format: "0500000US01001" -> "01001")
            const geo_id = props.GEO_ID || '';
            let fips_code = '';
            if (geo_id.startsWith('0500000US')) {
              fips_code = geo_id.slice(-5);
            } else {
              const state_code = props.STATE || '';
              const county_code = props.COUNTY || '';
              if (state_code && county_code) {
                fips_code = state_code + county_code;
              }
            }
            
            if (fips_code) {
              console.log(`Loading facilities for FIPS ${fips_code}`);
              const response = await fetch(`${process.env.REACT_APP_API_URL}/facilities/by-fips/${fips_code}`);
              
              if (response.ok) {
                const data = await response.json();
                setCountyFacilities(data.facilities || []);
                console.log(`Loaded ${data.facilities?.length || 0} facilities for ${data.county}, ${data.state} (FIPS: ${fips_code})`);
                
                // Log activity for facility view
                if (user) {
                  logActivity('county_facilities_viewed', user, undefined, 0, 0);
                }
              } else {
                console.warn(`Failed to load facilities for FIPS ${fips_code}:`, response.status);
                setCountyFacilities([]);
              }
            } else {
              setCountyFacilities([]);
              console.warn('Could not determine FIPS code for county');
            }
          } else {
            // For states, load sample facilities and release data
            const stateName = props.NAME || 'Unknown';
            console.log(`Loading sample data for state: ${stateName}`);
            
            try {
              // Load sample facilities for this state
              const facilityResponse = await fetch(`${process.env.REACT_APP_API_URL}/facilities/by-state/${encodeURIComponent(stateName)}?limit=50`);
              
              if (facilityResponse.ok) {
                const facilityData = await facilityResponse.json();
                setCountyFacilities(facilityData.facilities || []);
                console.log(`Loaded ${facilityData.facilities?.length || 0} sample facilities for ${stateName}`);
              } else {
                console.warn(`Failed to load sample facilities for ${stateName}:`, facilityResponse.status);
                setCountyFacilities([]);
              }
            } catch (error) {
              console.error(`Error loading sample facilities for ${stateName}:`, error);
              setCountyFacilities([]);
            }
          }
        } catch (error) {
          console.error('Error loading county facilities:', error);
          setCountyFacilities([]);
        }
      };
      
      loadAreaFacilities();
      
      // Log area selection activity
      if (user) {
        logActivity(boundaryLevel === 'counties' ? 'county_selected' : 'state_selected', user);
      }
    } else {
      console.warn('County click but no feature properties found');
    }
  };

  // Handle mouse hover
  const handleMouseMove = (event: any) => {
    const feature = event.features?.[0];
    if (feature?.properties) {
      const props = feature.properties as CountyProperties;
      setHoveredCounty(props);
    } else {
      setHoveredCounty(null);
    }
  };

  // Handle metric selection change
  const handleMetricChange = (newMetric: string) => {
    console.log(`Metric changed from ${selectedMetric} to ${newMetric}`);
    setSelectedMetric(newMetric);
    if (user) {
      logActivity('county_metric_change', user);
    }
  };

  // Handle color scale change
  const handleColorScaleChange = (newScale: string) => {
    console.log(`Color scale changed from ${colorScale} to ${newScale}`);
    setColorScale(newScale);
    if (user) {
      logActivity('map_color_scale_change', user);
    }
  };

  // Handle map style change
  const handleMapStyleChange = (newStyle: string) => {
    console.log(`Map style changed from ${mapStyle} to ${newStyle}`);
    setMapStyle(newStyle);
    if (user) {
      logActivity('county_map_style_change', user);
    }
  };


  // Handle boundary level change
  const handleBoundaryLevelChange = (newLevel: string) => {
    console.log(`Boundary level changed from ${boundaryLevel} to ${newLevel}`);
    setBoundaryLevel(newLevel);
    setSelectedCounty(null); // Clear selection when changing levels
    setCountyFacilities([]);
    if (user) {
      logActivity('boundary_level_change', user);
    }
  };

  // Get Mapbox style URL
  const getMapboxStyleUrl = (style: string): string => {
    const styles = {
      'light': 'mapbox://styles/mapbox/light-v11',
      'dark': 'mapbox://styles/mapbox/dark-v11', 
      'satellite': 'mapbox://styles/mapbox/satellite-v9',
      'streets': 'mapbox://styles/mapbox/streets-v12'
    };
    return styles[style as keyof typeof styles] || styles['light'];
  };

  // Calculate color scale values based on selected method
  const getColorScaleExpression = () => {
    if (!geoData?.features) return ['get', selectedMetric];

    // Extract all values for the selected metric
    const allValues = geoData.features
      .map((f: any) => f.properties[selectedMetric])
      .filter((val: any) => val != null && val > 0)
      .sort((a: number, b: number) => a - b);

    if (allValues.length === 0) return ['get', selectedMetric];

    let minVal: number, maxVal: number, midVal: number;

    switch (colorScale) {
      case 'percentile':
        // Use 5th to 95th percentile to avoid extreme outliers
        const p5 = Math.max(0, allValues[Math.floor(allValues.length * 0.05)]);
        const p95 = allValues[Math.floor(allValues.length * 0.95)];
        const p50 = allValues[Math.floor(allValues.length * 0.50)];
        minVal = p5;
        maxVal = p95;
        midVal = p50;
        break;

      case 'quartile':
        // Use quartiles for more balanced distribution
        const q1 = allValues[Math.floor(allValues.length * 0.25)];
        const q3 = allValues[Math.floor(allValues.length * 0.75)];
        const median = allValues[Math.floor(allValues.length * 0.50)];
        minVal = q1;
        maxVal = q3;
        midVal = median;
        break;

      case 'log':
        // Logarithmic scale for highly skewed data
        const logMin = Math.log(Math.max(0.001, allValues[0]));
        const logMax = Math.log(allValues[allValues.length - 1]);
        const logMid = (logMin + logMax) / 2;
        return [
          'case',
          ['>', ['get', selectedMetric], 0],
          [
            'interpolate',
            ['linear'],
            ['log10', ['max', 0.001, ['get', selectedMetric]]],
            logMin, '#f0f0f0',
            logMid * 0.7, '#e3f2fd',
            logMid, '#ffecb3',
            logMid * 1.3, '#ffab91',
            logMax, '#ef5350'
          ],
          '#f0f0f0'
        ];

      default: // 'linear'
        minVal = 0;
        maxVal = allValues[allValues.length - 1];
        midVal = maxVal * 0.5;
        break;
    }

    return [
      'case',
      ['>', ['get', selectedMetric], 0],
      [
        'interpolate',
        ['linear'],
        ['get', selectedMetric],
        minVal, '#f0f0f0',
        midVal * 0.5, '#e3f2fd',
        midVal * 0.75, '#bbdefb',
        midVal, '#ffecb3',
        midVal * 1.25, '#ffab91',
        maxVal, '#ef5350'
      ],
      '#f0f0f0'
    ];
  };

  // Metric labels (updated for dynamic backend response)
  const metricLabels: Record<string, string> = {
    leak_rate: 'Leak Rate (%)',
    total_facilities: 'Total Facilities',
    total_tanks: 'Total Tanks', 
    active_tanks: 'Active Tanks',
    avg_tank_age: 'Average Tank Age (years)',
    max_tank_age: 'Maximum Tank Age (years)',
    avg_protection_score: 'Average Protection Score',
    avg_risk_score: 'Average Risk Score',
    total_capacity: 'Total Capacity (gallons)',
    facilities_with_leaks: 'Facilities with Leaks',
    total_leaks: 'Total Leaks'
  };

  if (!isAuthenticated) {
    return (
      <div className="container">
        <p>Please log in to access the county map.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-container">
          <div>Loading USA county map and facility data...</div>
        </div>
      </div>
    );
  }

  if (!MAPBOX_TOKEN) {
    return (
      <div className="container">
        <div style={{ padding: '2rem', color: 'red' }}>
          <h3>Missing Mapbox API Key</h3>
          <p>Please configure REACT_APP_MAPBOX_PUBLIC_KEY in your environment variables.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ marginBottom: '1rem' }}>
        <h2>🗺️ USA Storage Tank Risk Analysis</h2>
        <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
          Interactive {boundaryLevel}-level view with {geoData?.features?.length || 0} {boundaryLevel} across the United States
        </p>
        
        <div style={{ 
          display: 'flex', 
          gap: '2rem', 
          marginBottom: '1rem', 
          flexWrap: 'wrap' 
        }}>
          
          <div>
            <label htmlFor="boundary-level-select" style={{ marginRight: '1rem', fontWeight: '500' }}>
              Boundary Level:
            </label>
            <select
              id="boundary-level-select"
              value={boundaryLevel}
              onChange={(e) => handleBoundaryLevelChange(e.target.value)}
              style={{ 
                padding: '0.5rem', 
                borderRadius: '4px', 
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                minWidth: '150px'
              }}
            >
              <option value="states">States</option>
              <option value="counties">Counties</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="metric-select" style={{ marginRight: '1rem', fontWeight: '500' }}>
              Color Code:
            </label>
            <select
              id="metric-select"
              value={selectedMetric}
              onChange={(e) => handleMetricChange(e.target.value)}
              style={{ 
                padding: '0.5rem', 
                borderRadius: '4px', 
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                minWidth: '200px'
              }}
            >
              {Object.entries(metricLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div style={{ position: 'relative', height: '600px', marginBottom: '1rem' }}>
        <Map
          mapboxAccessToken={MAPBOX_TOKEN}
          initialViewState={{
            longitude: -98.5,
            latitude: 39.8,
            zoom: 4
          }}
          style={{ width: '100%', height: '100%', borderRadius: '16px', overflow: 'hidden' }}
          mapStyle={getMapboxStyleUrl(mapStyle)}
          onMouseMove={handleMouseMove}
          onClick={handleAreaClick}
          interactiveLayerIds={['counties']}
        >
          {geoData && (
            <Source id="counties" type="geojson" data={geoData}>
              <Layer
                id="counties"
                type="fill"
                paint={{
                  'fill-color': getColorScaleExpression() as any,
                  'fill-opacity': 0.7,
                  'fill-outline-color': '#ffffff'
                }}
              />
              <Layer
                id="counties-border"
                type="line"
                paint={{
                  'line-color': '#ffffff',
                  'line-width': 0.5
                }}
              />
            </Source>
          )}
        </Map>

        {hoveredCounty && (
          <div 
            style={{ 
              position: 'absolute', 
              left: '10px', 
              top: '10px', 
              zIndex: 1000,
              backgroundColor: 'white',
              padding: '1rem',
              borderRadius: '16px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              maxWidth: '300px'
            }}
          >
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem' }}>
              {boundaryLevel === 'counties' 
                ? `${hoveredCounty.NAME}, ${hoveredCounty.state || hoveredCounty.STATE || 'Unknown'}`
                : hoveredCounty.NAME || 'Unknown'
              }
            </h3>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              <div>Facilities: {hoveredCounty.total_facilities?.toLocaleString() || 0}</div>
              <div>Facilities w/ Leaks: {hoveredCounty.facilities_with_leaks?.toLocaleString() || 0}</div>
              <div>Leak Rate: {(hoveredCounty.leak_rate || 0).toFixed(1)}%</div>
              <div>Total Tanks: {hoveredCounty.total_tanks?.toLocaleString() || 0}</div>
              <div>Avg Tank Age: {hoveredCounty.avg_tank_age?.toFixed(1) || 0} years</div>
              <div>Protection Score: {hoveredCounty.avg_protection_score?.toFixed(1) || 0}</div>
              {boundaryLevel === 'states' && hoveredCounty.has_release_data && (
                <>
                  <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                    <strong>Release Data:</strong>
                  </div>
                  <div>Total Releases: {hoveredCounty.total_releases?.toLocaleString() || 0}</div>
                  <div>Open Releases: {hoveredCounty.open_releases?.toLocaleString() || 0}</div>
                  <div>Open Release Rate: {(hoveredCounty.open_release_rate || 0).toFixed(1)}%</div>
                  <div style={{ fontWeight: '600', color: '#dc2626' }}>
                    Revised Leak Rate: {
                      hoveredCounty.total_releases && hoveredCounty.total_facilities 
                        ? ((hoveredCounty.total_releases / hoveredCounty.total_facilities) * 100).toFixed(1)
                        : '0.0'
                    }%
                  </div>
                </>
              )}
              <div style={{ 
                marginTop: '0.5rem', 
                padding: '0.25rem',
                fontSize: '0.75rem',
                fontStyle: 'italic',
                color: '#374151',
                textAlign: 'center',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '4px'
              }}>
                Click to view data
              </div>
            </div>
          </div>
        )}

        {/* Legend positioned on bottom right of map */}
        <div style={{ 
          position: 'absolute',
          bottom: '10px',
          right: '10px',
          zIndex: 1000,
          padding: '0.75rem',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '16px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(4px)'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600' }}>
            {metricLabels[selectedMetric]}
          </h4>
          <div style={{ position: 'relative' }}>
            <div style={{ 
              width: '120px', 
              height: '12px', 
              background: 'linear-gradient(to right, #f0f0f0, #e3f2fd, #bbdefb, #ffecb3, #ffab91, #ef5350)',
              borderRadius: '2px',
              border: '1px solid #ddd'
            }}></div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              width: '120px',
              fontSize: '0.75rem',
              marginTop: '0.25rem',
              color: '#6b7280'
            }}>
              <span>Low</span>
              <span>High</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        gap: '2rem', 
        marginBottom: '1rem', 
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        <div>
          <label htmlFor="color-scale-select" style={{ marginRight: '1rem', fontWeight: '500' }}>
            Color Scale:
          </label>
          <select
            id="color-scale-select"
            value={colorScale}
            onChange={(e) => handleColorScaleChange(e.target.value)}
            style={{ 
              padding: '0.5rem', 
              borderRadius: '4px', 
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              minWidth: '150px'
            }}
          >
            <option value="percentile">Percentile (5th-95th)</option>
            <option value="linear">Linear (0 to Max)</option>
            <option value="quartile">Quartile (Q1-Q3)</option>
            <option value="log">Logarithmic</option>
          </select>
        </div>

        <div>
          <label htmlFor="map-style-select" style={{ marginRight: '1rem', fontWeight: '500' }}>
            Map Style:
          </label>
          <select
            id="map-style-select"
            value={mapStyle}
            onChange={(e) => handleMapStyleChange(e.target.value)}
            style={{ 
              padding: '0.5rem', 
              borderRadius: '4px', 
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              minWidth: '150px'
            }}
          >
            <option value="streets">🛣️ Streets</option>
            <option value="light">☀️ Light</option>
            <option value="dark">🌙 Dark</option>
            <option value="satellite">🛰️ Satellite</option>
          </select>
        </div>
      </div>

      {selectedCounty && (
        <div style={{ 
          backgroundColor: '#f9fafb', 
          padding: '1.5rem', 
          borderRadius: '16px',
          marginBottom: '1rem'
        }}>
          <h2 style={{ margin: '0 0 1rem 0' }}>
            {boundaryLevel === 'counties' 
              ? `${selectedCounty.NAME}, ${selectedCounty.state || selectedCounty.STATE || 'Unknown'}`
              : selectedCounty.NAME || 'Unknown'
            }
          </h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div>
              <span style={{ fontWeight: '500' }}>Facilities:</span>
              <span style={{ marginLeft: '0.5rem' }}>
                {selectedCounty.total_facilities?.toLocaleString() || 0}
              </span>
            </div>
            <div>
              <span style={{ fontWeight: '500' }}>Facilities w/ Leaks:</span>
              <span style={{ marginLeft: '0.5rem' }}>
                {selectedCounty.facilities_with_leaks?.toLocaleString() || 0}
              </span>
            </div>
            <div>
              <span style={{ fontWeight: '500' }}>Leak Rate:</span>
              <span style={{ marginLeft: '0.5rem' }}>
                {(selectedCounty.leak_rate || 0).toFixed(1)}%
              </span>
            </div>
            <div>
              <span style={{ fontWeight: '500' }}>Total Tanks:</span>
              <span style={{ marginLeft: '0.5rem' }}>
                {selectedCounty.total_tanks?.toLocaleString() || 0}
              </span>
            </div>
            <div>
              <span style={{ fontWeight: '500' }}>Active Tanks:</span>
              <span style={{ marginLeft: '0.5rem' }}>
                {selectedCounty.active_tanks?.toLocaleString() || 0}
              </span>
            </div>
            <div>
              <span style={{ fontWeight: '500' }}>Avg Tank Age:</span>
              <span style={{ marginLeft: '0.5rem' }}>
                {selectedCounty.avg_tank_age?.toFixed(1) || 0} years
              </span>
            </div>
            <div>
              <span style={{ fontWeight: '500' }}>Protection Score:</span>
              <span style={{ marginLeft: '0.5rem' }}>
                {selectedCounty.avg_protection_score?.toFixed(1) || 0}/100
              </span>
            </div>
            {boundaryLevel === 'states' && selectedCounty.has_release_data && (
              <>
                <div style={{ 
                  gridColumn: '1 / -1',
                  borderTop: '1px solid #d1d5db',
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Release Information
                </div>
                <div>
                  <span style={{ fontWeight: '500' }}>Total Releases:</span>
                  <span style={{ marginLeft: '0.5rem' }}>
                    {selectedCounty.total_releases?.toLocaleString() || 0}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '500' }}>Open Releases:</span>
                  <span style={{ marginLeft: '0.5rem' }}>
                    {selectedCounty.open_releases?.toLocaleString() || 0}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '500' }}>Closed Releases:</span>
                  <span style={{ marginLeft: '0.5rem' }}>
                    {selectedCounty.closed_releases?.toLocaleString() || 0}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '500' }}>Open Release Rate:</span>
                  <span style={{ marginLeft: '0.5rem' }}>
                    {(selectedCounty.open_release_rate || 0).toFixed(1)}%
                  </span>
                </div>
              </>
            )}
          </div>

          <div>
            <h3 style={{ margin: '0 0 1rem 0' }}>
              {boundaryLevel === 'counties' 
                ? `Facilities in ${selectedCounty.NAME} County (${countyFacilities.length})`
                : `Sample Facilities in ${selectedCounty.NAME} (${countyFacilities.length}/${selectedCounty.total_facilities?.toLocaleString() || 0})`
              }
            </h3>
            
            {countyFacilities.length > 0 ? (
              <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse', 
                  fontSize: '0.9em',
                  backgroundColor: 'white'
                }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5' }}>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left', fontSize: '0.8em' }}>
                        Facility ID
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left', fontSize: '0.8em' }}>
                        Name
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left', fontSize: '0.8em' }}>
                        Address
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left', fontSize: '0.8em' }}>
                        City
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left', fontSize: '0.8em' }}>
                        County
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left', fontSize: '0.8em' }}>
                        State
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Zip Code
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Latitude
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Longitude
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        FIPS
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Mapping Method
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Has Leak
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Total Tanks
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Active Tanks
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Avg Tank Age
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Max Tank Age
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Protection Score
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Total Leaks
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '0.8em' }}>
                        Total Capacity
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {countyFacilities.map((facility, index) => (
                      <tr key={facility.Facility_ID} style={{ 
                        backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' 
                      }}>
                        <td style={{ border: '1px solid #ddd', padding: '4px', fontSize: '0.8em' }}>
                          {facility.Facility_ID || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', fontSize: '0.8em' }}>
                          {facility.Name || 'Unknown'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', fontSize: '0.8em' }}>
                          {facility.Address || 'No address'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', fontSize: '0.8em' }}>
                          {facility.City || 'Unknown'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', fontSize: '0.8em' }}>
                          {facility.County || 'Unknown'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', fontSize: '0.8em' }}>
                          {facility.State || 'Unknown'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.Zip_Code || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.Latitude ? facility.Latitude.toFixed(4) : 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.Longitude ? facility.Longitude.toFixed(4) : 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.FIPS || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.MAPPING_METHOD || 'N/A'}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '4px', 
                          textAlign: 'center',
                          fontSize: '0.8em',
                          backgroundColor: facility.HAS_LEAK ? '#ffebee' : '#e8f5e8',
                          color: facility.HAS_LEAK ? '#c62828' : '#2e7d32'
                        }}>
                          {facility.HAS_LEAK ? 'Yes' : 'No'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.TOTAL_TANK_COUNT || 0}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.ACTIVE_TANK_COUNT || 0}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.AVG_TANK_AGE ? `${facility.AVG_TANK_AGE.toFixed(1)} yrs` : 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.MAX_TANK_AGE ? `${facility.MAX_TANK_AGE.toFixed(1)} yrs` : 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.PROTECTION_SCORE ? `${facility.PROTECTION_SCORE.toFixed(1)}/100` : 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.TOTAL_LEAK_COUNT || 0}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center', fontSize: '0.8em' }}>
                          {facility.TOTAL_CAPACITY ? `${facility.TOTAL_CAPACITY.toLocaleString()} gal` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : boundaryLevel === 'counties' ? (
              <p>No individual facility data available for this county.</p>
            ) : (
              <p>Loading sample facility data for this state...</p>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default CountyMapView;